import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import type { Service } from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { deleteJSON } from '../internal/http';

const OkSchema = z.object({ ok: z.boolean() }).passthrough();

export function useDeleteService() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: async (serviceId: string) => {
      if (!orgId) throw new Error('No active organization');
      await deleteJSON(http, `/orgs/${orgId}/services/${serviceId}`, OkSchema);
      return serviceId;
    },
    onMutate: async (serviceId) => {
      if (!orgId) return { previous: undefined };
      const listKey = queryKeys.items.all(orgId);
      await queryClient.cancelQueries({ queryKey: listKey });
      // List cache is `InfiniteData<{ items, nextCursor? }>` since
      // the B1 (2026-05-09) migration. Walk every page so an
      // optimistic delete on row N still removes the entry
      // regardless of which page it lives on.
      type ServicesPage = { items: Service[]; nextCursor?: string };
      type ServicesCache = { pages: ServicesPage[]; pageParams: unknown[] };
      const previous = queryClient.getQueryData<ServicesCache>(listKey);
      queryClient.setQueryData<ServicesCache>(listKey, (cache) =>
        cache
          ? {
              ...cache,
              pages: cache.pages.map((page) => ({
                ...page,
                items: page.items.filter((s) => s.id !== serviceId),
              })),
            }
          : cache,
      );
      return { previous };
    },
    onError: (_err, _serviceId, context) => {
      if (orgId && context?.previous) {
        queryClient.setQueryData(queryKeys.items.all(orgId), context.previous);
      }
    },
    onSuccess: (serviceId) => {
      if (!orgId) return;
      queryClient.removeQueries({ queryKey: queryKeys.items.detail(orgId, serviceId) });
    },
  });

  return {
    deleteService: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
